import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	type PlayerIndex,
	reject,
	resultFromScores,
} from "../types";

export const DOTS_COUNT = 14;
/** The grid the dots are scattered on, so they never overlap. */
const GRID = 8;

export interface RaceDot {
	x: number;
	y: number;
}

export interface DotsRaceState extends BaseState {
	kind: "dotsrace";
	dots: RaceDot[];
	/** Whoever claimed each line, in order. `owner[i]` joins dot i to i+1. */
	owner: (PlayerIndex | null)[];
	/** The next dot to be joined. */
	next: number;
	lastNote: string | null;
}

export interface DotsRaceMove {
	dot: number;
}

export const dotsRace: GameDefinition<DotsRaceState, DotsRaceMove> = {
	id: "dotsrace",
	category: "art",
	title: "Connect-the-Dots Race",
	emoji: "✏️",
	blurb: "Join the numbers in order — and be the one who gets there first.",
	howTo: [
		"The dots are numbered 1 to 14.",
		"Both of you are racing to tap the next number in order.",
		"Whoever taps it first draws that line and scores.",
		"Most lines when the picture is finished wins.",
	],

	create(rng) {
		// One dot per grid cell, so nothing lands on top of anything else.
		const cells = rng.shuffle([...Array(GRID * GRID).keys()]).slice(0, DOTS_COUNT);
		const dots = cells.map((cell) => ({ x: cell % GRID, y: Math.floor(cell / GRID) }));

		return {
			kind: "dotsrace",
			dots,
			owner: Array<PlayerIndex | null>(DOTS_COUNT - 1).fill(null),
			next: 1,
			lastNote: null,
			// No turns: it is a race.
			turn: null,
			scores: [0, 0],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const dot = (raw as { dot?: unknown }).dot;
		if (typeof dot !== "number" || !Number.isInteger(dot)) return null;
		if (dot < 0 || dot >= DOTS_COUNT) return null;
		return { dot };
	},

	apply(state, player, move): MoveOutcome<DotsRaceState> {
		if (state.result) return reject("This game is already finished.");
		// Tapping the wrong dot simply does nothing — no penalty for a child
		// whose finger landed badly, and no way to spoil the other player's race.
		if (move.dot !== state.next) return reject("That's not the next number.");

		const owner = [...state.owner];
		owner[state.next - 1] = player;
		const scores: [number, number] = [...state.scores];
		scores[player] += 1;

		const next = state.next + 1;
		const finished = next >= DOTS_COUNT;

		return {
			ok: true,
			state: {
				...state,
				owner,
				next: finished ? state.next : next,
				scores,
				lastNote: `Line ${state.next} drawn!`,
				turn: null,
				result: finished ? resultFromScores(scores) : null,
			},
		};
	},

	legalMoves(state) {
		if (state.result) return [];
		// Exactly one dot is worth tapping at any moment.
		return [{ dot: state.next }];
	},

	tick() {
		return null;
	},

	nextTickAt() {
		return null;
	},
};
